import { formatDateUtc, formatNumber, formatShortMonthYear } from "@/lib/i18n";

interface CohortRetentionCell {
  period: number;
  activeCustomers: number;
  retentionRate: number;
  revenueCents: number;
}

interface CohortRetentionRow {
  cohortStart: string;
  cohortSize: number;
  cells: CohortRetentionCell[];
}

function formatWholePercent(value: number): string {
  return `${formatNumber(Math.round(value * 100), "en")}%`;
}

function formatCohortLabel(value: string, granularity: "week" | "month"): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (granularity === "week") {
    return `Week of ${formatDateUtc(date, "en")}`;
  }

  return formatShortMonthYear(date, "en");
}

function getCellTone(retentionRate: number): string {
  if (retentionRate >= 0.75) return "bg-emerald-700 text-white";
  if (retentionRate >= 0.5) return "bg-emerald-600 text-white";
  if (retentionRate >= 0.3) return "bg-emerald-500 text-white";
  if (retentionRate > 0) return "bg-emerald-100 text-emerald-950";
  return "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]";
}

export function CohortTable({
  rows,
  periods,
  granularity,
}: {
  rows: CohortRetentionRow[];
  periods: number[];
  granularity: "week" | "month";
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] bg-[var(--color-background)]">
            <th className="px-4 py-3 text-left font-semibold text-[var(--color-foreground)]">Cohort</th>
            <th className="px-4 py-3 text-left font-semibold text-[var(--color-foreground)]">Size</th>
            {periods.map((period) => (
              <th key={period} className="px-3 py-3 text-center font-semibold text-[var(--color-foreground)]">
                {granularity === "week" ? `W${period}` : `M${period}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const cellMap = new Map(row.cells.map((cell) => [cell.period, cell]));

            return (
              <tr key={row.cohortStart} className="border-b border-[var(--color-border)] last:border-b-0">
                <td className="px-4 py-3 font-medium text-[var(--color-foreground)]">
                  {formatCohortLabel(row.cohortStart, granularity)}
                </td>
                <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{formatNumber(row.cohortSize, "en")}</td>
                {periods.map((period) => {
                  const cell = cellMap.get(period);
                  return (
                    <td key={period} className="px-2 py-2 text-center">
                      {cell ? (
                        <div
                          className={`rounded-lg px-2 py-3 text-xs font-semibold transition-colors ${getCellTone(cell.retentionRate)}`}
                          title={`${formatWholePercent(cell.retentionRate)} retention • ${formatNumber(cell.activeCustomers, "en")} active customers • €${(cell.revenueCents / 100).toFixed(0)} revenue`}
                        >
                          {formatWholePercent(cell.retentionRate)}
                        </div>
                      ) : (
                        <div className="rounded-lg px-2 py-3 text-xs text-[var(--color-muted-foreground)]">-</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
